import app from '#app/app'
import { appLayout } from '#app/init_app_layout.svelte.ts'
import { assertString } from '#app/lib/assert_types'
import { isEmail } from '#app/lib/boolean_tests'
import { newError } from '#app/lib/error'
import { parseQuery } from '#app/lib/location'
import { getAllQuerystringParameters } from '#app/lib/querystring_helpers'
import { addRoutes } from '#app/lib/router'
import { treq } from '#app/lib/treq'
import { commands, reqres } from '#app/radio'
import type { ControllersAPI } from '#server/types/controllers_api'
import type { OAuthClientId } from '#server/types/oauth'
import { requestLogout } from '#user/lib/auth'
import { I18n } from '#user/lib/i18n'
import { mainUser } from '#user/lib/main_user'

export default {
  initialize () {
    addRoutes({
      '/signup(/)': 'showSignup',
      '/login(/)': 'showLogin',
      '/login/forgot-password(/)': 'showForgotPassword',
      '/login/reset-password(/)': 'showResetPassword',
      '/logout(/)': 'logout',
      '/authorize(/)': 'showAuthorizeMenu',
    }, controller)

    commands.setHandlers({
      'show:signup': controller.showSignup,
      'show:login': controller.showLogin,
      'show:forgot:password': controller.showForgotPassword,
    })
  },
}

function showAuth (name: string, label: string, Component, options: string | object) {
  if (!navigator.cookieEnabled) {
    return commands.execute('show:error:cookieRequired', `show:${name}`)
  }

  if (mainUser) return commands.execute('show:home')

  if (typeof options === 'string') options = parseQuery(options)
  appLayout.showChildComponent('main', Component, { props: options })
  app.navigate(name, { metadata: { title: I18n(label) } })
}

// beware that appLayoutis undefined when User.define is fired
// appLayoutshould thus appear only in callbacks
const controller = {
  // Options might be passed as an object when called by `commands.execute('show:signup)`
  async showSignup (options: string | object) {
    const { default: Signup } = await import('./components/signup.svelte')
    showAuth('signup', 'Create account', Signup, options)
  },

  async showLogin (options: string | object) {
    const { default: Login } = await import('./components/login.svelte')
    showAuth('login', 'login', Login, options)
  },

  async showForgotPassword (querystring: string) {
    try {
      const { default: ForgotPassword } = await import('./components/forgot_password.svelte')
      const { resetPasswordFail, email } = parseQuery(querystring)
      appLayout.showChildComponent('main', ForgotPassword, {
        props: {
          resetPasswordFail: typeof resetPasswordFail === 'boolean' ? resetPasswordFail : undefined,
          email: isEmail(email) ? email : undefined,
        },
      })
      app.navigate('login/forgot-password', {
        metadata: {
          title: I18n('forgot password'),
        },
      })
    } catch (err) {
      commands.execute('show:error', err)
    }
  },

  async showResetPassword () {
    const { default: ResetPassword } = await import('./components/reset_password.svelte')
    if (mainUser) {
      appLayout.showChildComponent('main', ResetPassword)
      app.navigate('login/reset-password', {
        metadata: {
          title: I18n('reset password'),
        },
      })
    } else {
      commands.execute('show:forgot:password')
    }
  },

  async showAuthorizeMenu () {
    const query = getAllQuerystringParameters()

    try {
      validateAuthorizationRequest(query)
      const postLoginRedirection = window.location.pathname + window.location.search
      if (!(reqres.request('require:loggedIn', postLoginRedirection))) return
      assertString(query.client_id)
      const client = await getOAuthClient(query.client_id)
      const { default: AuthorizeMenu } = await import('./components/authorize_menu.svelte')
      appLayout.showChildComponent('main', AuthorizeMenu, { props: { query, client } })
    } catch (err) {
      commands.execute('show:error', err)
    }
  },

  logout: requestLogout,
} as const

async function getOAuthClient (clientId: OAuthClientId) {
  const { clients } = await treq('oauth', 'get', 'clients-by-ids', { ids: [ clientId ] })
  return clients[clientId]
}

// Further validation will be done by the server
function validateAuthorizationRequest (query: object): asserts query is ControllersAPI['oauth']['get']['authorize']['params'] {
  // @ts-expect-error
  const { state, scope, client_id: clientId, redirect_uri: redirectUri } = query
  if (!clientId) throw invalidAuthorizationRequest('missing client_id')
  if (!scope) throw invalidAuthorizationRequest('missing scope')
  if (!state) throw invalidAuthorizationRequest('missing state')
  if (!redirectUri) throw invalidAuthorizationRequest('missing redirect_uri')
}

function invalidAuthorizationRequest (reason: string, query?: object) {
  return newError(`invalid authorization request: ${reason}`, 400, { reason, query })
}
